import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface OutageApiItem {
    slug: string;
    start_time: string;
    last_checked: string;
    total_reports: number;
    threshold: number;
    status: string;
    change_type: string;
}

export interface EnhancedOutage extends OutageApiItem {
    lastSeen: number;
    displayStatus: "active" | "clear";
    isRead: boolean;
}

export interface SetOutagesParams {
    outages: OutageApiItem[];
    allClear: boolean;
    timestamp: string;
}

export interface OutageInfoStore {
    outages: Record<string, EnhancedOutage>;

    allClear: boolean;
    timestamp?: string;

    setOutages: (params: SetOutagesParams) => void;
    markOutageAsRead: (slug: string) => void;
    markAllOutagesAsRead: () => void;

    getActiveOutages: () => EnhancedOutage[];
    getUnreadOutages: () => EnhancedOutage[];
    getAllVisibleOutages: () => EnhancedOutage[];
}

export const useOutageInfoStore = create<OutageInfoStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    outages: {},
                    allClear: true,
                    timestamp: undefined,

                    setOutages: ({ outages, allClear, timestamp }: SetOutagesParams) => {
                        set(
                            (state) => {
                                const now = Date.now();
                                const currentSlugs = new Set(outages.map((o) => o.slug));

                                // Step 1: Process current outages - add new ones and update existing ones
                                outages.forEach((outage) => {
                                    const existingOutage = state.outages[outage.slug];

                                    state.outages[outage.slug] = {
                                        ...outage,
                                        lastSeen: now,
                                        displayStatus: "active",
                                        isRead: existingOutage?.isRead ?? false,
                                    };
                                });

                                // Step 2: Process outages that are no longer in the API response
                                Object.keys(state.outages).forEach((slug) => {
                                    if (!currentSlugs.has(slug)) {
                                        const outage = state.outages[slug];

                                        // Mark active outages as cleared if they're no longer returned by the API
                                        if (outage.displayStatus === "active") {
                                            state.outages[slug] = {
                                                ...outage,
                                                displayStatus: "clear",
                                            };
                                        }

                                        // Remove cleared outages older than one hour
                                        const oneHourAgo = now - 60 * 60 * 1000;
                                        if (outage.displayStatus === "clear" && outage.lastSeen < oneHourAgo) {
                                            delete state.outages[slug];
                                        }
                                    }
                                });

                                // Step 3: Update metadata
                                state.allClear = allClear;
                                state.timestamp = timestamp;
                            },
                            false,
                            "setOutages",
                        );
                    },

                    markOutageAsRead: (slug: string) => {
                        set(
                            (state) => {
                                if (state.outages[slug]) {
                                    state.outages[slug].isRead = true;
                                }
                            },
                            false,
                            "markOutageAsRead",
                        );
                    },

                    markAllOutagesAsRead: () => {
                        set(
                            (state) => {
                                Object.keys(state.outages).forEach((slug) => {
                                    state.outages[slug].isRead = true;
                                });
                            },
                            false,
                            "markAllOutagesAsRead",
                        );
                    },

                    getActiveOutages: () => {
                        const state = get();
                        return Object.values(state.outages).filter((outage) => outage.displayStatus === "active");
                    },

                    getUnreadOutages: () => {
                        const state = get();
                        return Object.values(state.outages).filter((outage) => !outage.isRead);
                    },

                    getAllVisibleOutages: () => {
                        const state = get();
                        return Object.values(state.outages).sort((a, b) => {
                            // Sort order: active outages first, then by most recent start_time within each group
                            if (a.displayStatus !== b.displayStatus) {
                                return a.displayStatus === "active" ? -1 : 1;
                            }

                            const aDate = new Date(a.start_time);
                            const bDate = new Date(b.start_time);

                            return bDate.getTime() - aDate.getTime();
                        });
                    },
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "outage-info-store",
                    name: "outage-info",
                },
            ),
        ),
        {
            name: "outage-info-store",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                outages: state.outages,
                allClear: state.allClear,
                timestamp: state.timestamp,
            }),
        },
    ),
);
